import { useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { FieldValues, useController, useFormContext } from 'react-hook-form';

import {
    ICourseListItemFragment,
    CourseListItemFragmentDoc,
} from 'graphql/types';
import { AddButton } from 'common/components/Button/AddButton';
import {
    getCourseSelectStatus,
    isCourseAdded,
    isCourseSelected,
} from 'training/utils/courseSelect';
import { CourseListItem } from 'training/components/CourseListItem';
import { CourseSelectDrawer } from 'training/components/CourseSelectDrawer';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';

interface IProps {
    name: string;
}

export const CourseSelectorField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedCourses, setSelectedCourses] = useState<
        ICourseListItemFragment[]
    >([]);
    const client = useApolloClient();

    const control = asTypedControl<string[]>(name, formControl);

    const { setValue } = useFormContext();
    const {
        field: { value: addedCourseIds },
        fieldState: { error, isTouched },
    } = useController({
        name,
        control,
    });

    const addedCourses = useMemo(() => {
        // Get course objects from cache based on the course ids set in the form
        const courses =
            addedCourseIds
                ?.map((addedCourseId) => {
                    try {
                        return client.cache.readFragment<ICourseListItemFragment>(
                            {
                                id: `CourseListItem:${addedCourseId}`,
                                fragment: CourseListItemFragmentDoc,
                                fragmentName: 'CourseListItem',
                            }
                        );
                    } catch {
                        return null;
                    }
                })
                .filter(Boolean) || [];

        return courses as ICourseListItemFragment[];
    }, [addedCourseIds, client.cache]);

    const handleDrawerOpen = (open: boolean) => {
        if (!open) setSelectedCourses([]);

        setDrawerOpen(open);
    };

    const handleSelectCourse = (course: ICourseListItemFragment) => {
        const newCourses = [...selectedCourses, course].sort((a, b) =>
            a.title > b.title ? 1 : -1
        );

        setSelectedCourses(newCourses);
    };

    const handleRemoveCourse = (course: ICourseListItemFragment) => {
        const { isSelected } = getCourseSelectStatus(
            selectedCourses,
            addedCourses,
            course
        );

        if (isSelected) {
            const newSelectedCourses = selectedCourses.filter(
                (selectedCourse) => selectedCourse.id !== course.id
            );

            setSelectedCourses(newSelectedCourses);

            return;
        }

        setValue(
            name,
            addedCourses
                .filter((addedCourse) => addedCourse.id !== course.id)
                .map((course) => course.id),
            { shouldDirty: true }
        );
    };

    const handleAddCourses = () => {
        setValue(
            name,
            [
                ...(addedCourseIds || []),
                ...selectedCourses.map((selectedCourse) => selectedCourse.id),
            ],
            { shouldDirty: true }
        );

        setSelectedCourses([]);

        handleDrawerOpen(false);
    };

    return (
        <Box>
            <Box
                sx={{
                    alignItems: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <AddButton
                    aria-label={translate('addCoursesTitle')}
                    error={
                        !!error?.message && isTouched
                            ? error.message
                            : undefined
                    }
                    onClick={() => handleDrawerOpen(true)}
                >
                    {translate('addCoursesTitle')}
                </AddButton>

                {!!addedCourses.length && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            mt: 2,
                        }}
                    >
                        {addedCourses.map((addedCourse) => (
                            <CourseListItem
                                addButton
                                added={isCourseAdded(addedCourses, addedCourse)}
                                course={addedCourse}
                                key={`added-${addedCourse.id}`}
                                selected={isCourseSelected(
                                    selectedCourses,
                                    addedCourse
                                )}
                                onClick={(course: ICourseListItemFragment) =>
                                    handleRemoveCourse(course)
                                }
                            />
                        ))}
                    </Box>
                )}
            </Box>

            <CourseSelectDrawer
                addedCourses={addedCourses}
                open={drawerOpen}
                selectedCourses={selectedCourses}
                onAddCourses={handleAddCourses}
                onDrawerOpen={handleDrawerOpen}
                onRemoveCourse={handleRemoveCourse}
                onSelectCourse={handleSelectCourse}
            />
        </Box>
    );
};
